/**
 * Ability model.
 *
 * Each skill keeps a discrete posterior over "ability" on the 1–7 difficulty
 * scale. Ability θ means: tasks of difficulty θ are solved about 70 % of the
 * time without help. Evidence updates the posterior with a logistic response
 * model; time between evidence diffuses it (older evidence → less certainty),
 * which lets later evidence revise earlier estimates.
 *
 * The starting distribution is a single NEUTRAL regulariser that is identical
 * for every student and every skill. It is never derived from grade, stage,
 * curriculum membership or self-estimates.
 */

export const GRID: number[] = Array.from({ length: 29 }, (_, i) => 0.5 + i * 0.25);
const SLOPE = 1.7;

export function gaussian(mu: number, sd: number): number[] {
  const w = GRID.map((t) => Math.exp(-0.5 * ((t - mu) / sd) ** 2));
  return normalize(w);
}

/** Neutral regulariser: broad, centred mid-scale, same for everyone. */
export const neutralPrior = (): number[] => gaussian(3.5, 2.2);

export function normalize(p: number[]): number[] {
  const s = p.reduce((a, b) => a + b, 0) || 1;
  return p.map((v) => v / s);
}

/** Probability of success on an item of difficulty d for ability θ. */
export function pCorrect(theta: number, d: number, guess = 0): number {
  const p = 1 / (1 + Math.exp(-SLOPE * (theta - d + 0.5)));
  return guess + (1 - guess) * p;
}

/**
 * Bayesian update with partial credit (score ∈ [0,1]) and evidence weight
 * (tempered likelihood). Guess rate accounts for multiple-choice luck.
 */
export function update(post: number[], d: number, score: number, weight: number, guess = 0): number[] {
  const out = post.map((pr, i) => {
    const p = Math.min(0.995, Math.max(0.005, pCorrect(GRID[i], d, guess)));
    const like = Math.pow(p, score) * Math.pow(1 - p, 1 - score);
    return pr * Math.pow(like, weight);
  });
  return normalize(out);
}

/** Diffuse certainty after time without evidence (sd grows with √days). */
export function diffuse(post: number[], days: number): number[] {
  if (days <= 0.5) return post;
  const sigma = Math.min(0.6, 0.05 * Math.sqrt(days)) / 0.25; // in grid cells
  if (sigma < 0.2) return post;
  const radius = Math.ceil(sigma * 3);
  const kernel: number[] = [];
  for (let k = -radius; k <= radius; k++) kernel.push(Math.exp(-0.5 * (k / sigma) ** 2));
  const ks = kernel.reduce((a, b) => a + b, 0);
  const n = post.length;
  const out = new Array(n).fill(0);
  for (let i = 0; i < n; i++) {
    for (let k = -radius; k <= radius; k++) {
      let j = i + k;
      if (j < 0) j = -j - 1;
      if (j >= n) j = 2 * n - j - 1;
      out[i] += post[Math.min(n - 1, Math.max(0, j))] * (kernel[k + radius] / ks);
    }
  }
  return normalize(out);
}

export function mean(post: number[]): number {
  return post.reduce((s, p, i) => s + p * GRID[i], 0);
}

export function sd(post: number[]): number {
  const m = mean(post);
  return Math.sqrt(post.reduce((s, p, i) => s + p * (GRID[i] - m) ** 2, 0));
}

export function quantile(post: number[], q: number): number {
  let acc = 0;
  for (let i = 0; i < post.length; i++) {
    acc += post[i];
    if (acc >= q) return GRID[i];
  }
  return GRID[GRID.length - 1];
}

function entropy(p: number[]): number {
  let h = 0;
  for (const v of p) if (v > 1e-12) h -= v * Math.log(v);
  return h;
}

/** Expected information gain (nats) from asking an item of difficulty d. */
export function expectedInfoGain(post: number[], d: number, guess = 0): number {
  let p1 = 0;
  const a: number[] = [];
  const b: number[] = [];
  post.forEach((pr, i) => {
    const p = pCorrect(GRID[i], d, guess);
    p1 += pr * p;
    a.push(pr * p);
    b.push(pr * (1 - p));
  });
  const h0 = entropy(post);
  const h1 = entropy(normalize(a));
  const h2 = entropy(normalize(b));
  return h0 - (p1 * h1 + (1 - p1) * h2);
}

/** Predicted success probability on difficulty d under the posterior. */
export function predictSuccess(post: number[], d: number, guess = 0): number {
  return post.reduce((s, p, i) => s + p * pCorrect(GRID[i], d, guess), 0);
}

/**
 * The hardest meaningful difficulty: highest d (within range) whose
 * predicted success is still at least `target` (default ≈ 0.65).
 */
export function edgeDifficulty(post: number[], range: [number, number], target = 0.65): number {
  let best = range[0];
  for (let d = range[0]; d <= range[1]; d++) {
    if (predictSuccess(post, d) >= target) best = d;
  }
  return best;
}
